import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Send, CheckCircle, MessageSquare, Lightbulb, Bug, Heart, AlertTriangle } from 'lucide-react';
import { captureError } from '@/lib/sentry';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CONTACT_API_BASE = import.meta.env.REACT_APP_CONTACT_API_URL || '';
const API_ENDPOINT = `${CONTACT_API_BASE}/contact`;

type FeedbackType = 'general' | 'feature' | 'bug' | 'love';

const feedbackTypes = [
  { id: 'general' as FeedbackType, labelKey: 'feedback.typeGeneral', icon: MessageSquare, color: 'blue' },
  { id: 'feature' as FeedbackType, labelKey: 'feedback.typeFeature', icon: Lightbulb, color: 'amber' },
  { id: 'bug' as FeedbackType, labelKey: 'feedback.typeBug', icon: Bug, color: 'red' },
  { id: 'love' as FeedbackType, labelKey: 'feedback.typeLove', icon: Heart, color: 'pink' },
];

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    message: '',
    feedbackType: 'general' as FeedbackType,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    const selectedType = feedbackTypes.find(ft => ft.id === formData.feedbackType);

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.email || 'Anonymous User',
          email: formData.email || 'anonymous@feedback.systemdox.com',
          company: `Feedback Type: ${selectedType ? t(selectedType.labelKey) : t('feedback.typeGeneral')}`,
          message: formData.message,
          requestType: 'Product Feedback',
        }),
      });

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        const data = await response.json();
        setError(data.error || t('feedback.defaultError'));
      }
    } catch (err) {
      captureError(err as Error, {
        component: 'FeedbackModal',
        action: 'submit_feedback',
        feedbackType: formData.feedbackType,
      });
      setError(t('feedback.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ email: '', message: '', feedbackType: 'general' });
    setIsSubmitted(false);
    setError('');
    onClose();
  };

  const getTypeColor = (type: FeedbackType, isSelected: boolean) => {
    const colors = {
      general: isSelected ? 'bg-blue-100 border-blue-400 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300',
      feature: isSelected ? 'bg-amber-100 border-amber-400 text-amber-700' : 'bg-white border-slate-200 text-slate-600 hover:border-amber-300',
      bug: isSelected ? 'bg-red-100 border-red-400 text-red-700' : 'bg-white border-slate-200 text-slate-600 hover:border-red-300',
      love: isSelected ? 'bg-pink-100 border-pink-400 text-pink-700' : 'bg-white border-slate-200 text-slate-600 hover:border-pink-300',
    };
    return colors[type];
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative bg-white rounded-2xl max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <X size={20} />
        </button>

        {isSubmitted ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-emerald-600" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">{t('feedback.thankYou')}</h3>
            <p className="text-slate-600 mb-6">
              {t('feedback.thankYouMessage')}
            </p>
            <button
              onClick={handleClose}
              className="px-6 py-3 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors"
            >
              {t('feedback.close')}
            </button>
          </div>
        ) : (
          <div className="p-8">
            {/* Header with validation notice */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <span className="text-sm font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded-full">
                  {t('feedback.earlyAccess')}
                </span>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                {t('feedback.shareYourFeedback')}
              </h3>
              <p className="text-slate-600" dangerouslySetInnerHTML={{ __html: t('feedback.conceptValidation') }} />
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Feedback Type Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('feedback.whatType')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {feedbackTypes.map((type) => {
                    const Icon = type.icon;
                    const isSelected = formData.feedbackType === type.id;
                    return (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, feedbackType: type.id })}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 font-medium text-sm transition-all ${getTypeColor(type.id, isSelected)}`}
                      >
                        <Icon className="w-4 h-4" />
                        {t(type.labelKey)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Feedback Message */}
              <div>
                <label htmlFor="feedback-message" className="block text-sm font-medium text-slate-700 mb-1">
                  {t('feedback.yourFeedbackLabel')}
                </label>
                <textarea
                  id="feedback-message"
                  required
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                  placeholder={
                    formData.feedbackType === 'feature'
                      ? t('feedback.placeholderFeature')
                      : formData.feedbackType === 'bug'
                      ? t('feedback.placeholderBug')
                      : formData.feedbackType === 'love'
                      ? t('feedback.placeholderLove')
                      : t('feedback.placeholderGeneral')
                  }
                />
              </div>

              {/* Email (Optional) */}
              <div>
                <label htmlFor="feedback-email" className="block text-sm font-medium text-slate-700 mb-1">
                  {t('feedback.emailLabel')} <span className="text-slate-400 font-normal">{t('feedback.emailOptional')}</span>
                </label>
                <input
                  type="email"
                  id="feedback-email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder={t('feedback.emailPlaceholder')}
                />
                <p className="mt-1 text-xs text-slate-500">
                  {t('feedback.leaveBlank')}
                </p>
              </div>

              {error && (
                <p className="text-red-600 text-sm">{error}</p>
              )}

              <button
                type="submit"
                disabled={isSubmitting || !formData.message.trim()}
                className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-cyan-500 text-white rounded-xl font-semibold text-lg transition-all shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  t('feedback.sending')
                ) : (
                  <>
                    <Send size={20} />
                    {t('feedback.sendFeedback')}
                  </>
                )}
              </button>

              <p className="text-center text-xs text-slate-500">
                {t('feedback.helpsUs')}
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
